import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Loading from '../../components/Loading';
import CartOrderSummary from '../../components/OrderSummary';
import {
  type CartApiItems,
  type CartDetailType,
  OutOfStock,
  type GuestCartResponse,
} from '../../interface/interface';
import { calculateOutOfStockEnum } from '../../utils/outOfStockStatus';
import { EmptyCartIcon } from '../../assets/svgImages';
import showToast from '../../components/ToastComponent';
import getGuestCart from '../../service/getGuestCart';
import addGuestProductCart from '../../service/addGuestProductCart';
import removeGuestProductFromCart from '../../service/removeGuestProductFromCart';
import GuestShippingDetails from '../../components/guest/GuestShippingDetails';
import {
  GuestCartProvider,
  useGuestCart,
} from '../../context/guestCartContext';
import LoadingButton from '../../components/LoadingButton';
import Breadcrumbs from '../../components/Breadcrumbs';
import { callProducts } from '../../service/callProducts';
import { callAccessories } from '../../service/callAccessories';
import { createGuestPayUrl } from '../../service/createGuestPayUrl';
import FloatingCart from '../../components/FloatingCart';
import isMobile from 'ismobilejs';
import FloatingCartMobile from '../../components/FloatingCartMobile';

const GuestCartContent = () => {
  const [pageRefresh, setPageRefresh] = useState<boolean>(false);
  const [cart, setCart] = useState<CartDetailType | null>(null);
  const { refreshCart } = useGuestCart();
  const [payoutLoading, setPayoutLoading] = useState<boolean>(false);
  const [shippingEditDetailEnabled, setEditDetailEnabled] =
    useState<boolean>(false);
  const [shippingConfirmChecked, setShippingConfirmChecked] =
    useState<boolean>(false);
  const [userConsent, setUserConsent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  async function addProductToCart(id: string) {
    try {
      setLoading(true);
      const result = await addGuestProductCart(id, 1);

      if (result?.success) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });

        // Refresh both cart data and context
        await Promise.all([callCart(), refreshCart()]);
        setLoading(false);
      } else {
        showToast({
          message: result?.message || 'Failed to add item',
          type: 'warning',
          timeout: 6000,
        });
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Failed to add item',
        type: 'error',
      });
      setLoading(false);
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      const result = await removeGuestProductFromCart(id);

      if (result?.success) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });

        // Refresh both cart data and context
        await Promise.all([callCart(), refreshCart()]);
      } else {
        showToast({
          message: result?.message || 'Unable to remove item',
          type: 'warning',
        });
      }
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Unable to remove item',
        type: 'error',
      });
    }
  }

  const enableEditShipping = useMemo(
    () => () => {
      setEditDetailEnabled(!shippingEditDetailEnabled);
    },
    [shippingEditDetailEnabled],
  );

  const callCart = useMemo(
    () => async () => {
      try {
        const cartResponse: GuestCartResponse = await getGuestCart();

        if (!cartResponse || !cartResponse.success || !cartResponse.cart) {
          setCart(null);
          return false;
        }

        const products = await callProducts();
        const accessories = await callAccessories();
        const allProducts = [...products, ...accessories];

        const lineItems: CartApiItems[] =
          cartResponse.cart.line_items?.map((item: any) => {
            const relatedProduct: any | undefined = allProducts?.find(
              (product) =>
                String(product.product_code) === String(item.product_id),
            );

            if (!relatedProduct) {
              return {
                ...item,
                max_quantity: 0,
                outOfStock: OutOfStock.withNoSubs,
                description: item.title || 'Product not found',
                imageUrl: '/placeholder.svg?height=100&width=100',
                requiresPrescription: false,
                isProductOutOfStock: true,
                prescription: {} as any,
              };
            }

            return {
              ...item,
              max_quantity:
                relatedProduct?.productMaxQty ||
                relatedProduct?.inventory_quantity ||
                0,
              outOfStock: calculateOutOfStockEnum(
                relatedProduct,
                item.quantity,
              ),
              description:
                relatedProduct?.description ||
                relatedProduct?.name ||
                item.title,
              imageUrl:
                relatedProduct?.image_url ||
                '/placeholder.svg?height=100&width=100',
              requiresPrescription: false,
              isProductOutOfStock: relatedProduct?.inventory_quantity === 0,
              prescription: {} as any,
            };
          }) || [];

        const cartData: CartDetailType = {
          id: cartResponse.cart.id,
          subtotal_price: cartResponse.cart.subtotal_price,
          total_price: cartResponse.cart.total_price,
          line_items: lineItems,
          web_url: cartResponse.cart.web_url || '',
          shipping_address: cartResponse.cart.shipping_address,
          shipping_rate: cartResponse.cart.shipping_rate,
        };

        setCart(cartData);
        setEditDetailEnabled(!cartData?.shipping_address?.first_name);
        return true;
      } catch (error: any) {
        console.error('Error in callCart:', error);
        showToast({
          message: 'Unable to fetch updated cart!',
          type: 'error',
        });
        setCart(null);
        return false;
      }
    },
    [],
  );

  const handleEmptyCart = async () => {
    setPageRefresh(true);

    try {
      // Clear all cart-related data from localStorage
      sessionStorage.removeItem('guestCartId');
      sessionStorage.removeItem('guestEmail');
      sessionStorage.removeItem('guestCart');

      // Dispatch a storage event to notify other components
      window.dispatchEvent(new Event('storage'));

      setCart(null);
      await refreshCart(); // Update context as well

      showToast({
        message: 'Your cart has been emptied.',
        type: 'success',
      });
    } catch (error) {
      showToast({
        message: 'Unable to clear cart',
        type: 'info',
      });
    }

    setPageRefresh(false);
  };

  const proceedToPay = useMemo(
    () => async () => {
      setPayoutLoading(true);
      const urlResponse = await createGuestPayUrl();
      if (typeof urlResponse === 'number') {
        showToast({
          message: 'Error while get payurl',
          type: 'error',
        });
        return;
      }
      const payUrl = urlResponse?.data;

      if (typeof payUrl === 'object') {
        setPayoutLoading(false);
        showToast({
          message: (payUrl as { message: string }).message,
          type: 'info',
        });
      } else {
        setPayoutLoading(false);
        window.open(payUrl, '_self');
      }
    },
    [],
  );

  const { isFetching: cartDataLoading } = useQuery({
    queryKey: ['guestCartList'],
    queryFn: callCart,
  });

  const loadedData = (
    <div className="pb-4">
      <div className="flex justify-between mt-2 mb-5 items-center">
        <h2 className="text-base xl:text-xl text-primary-blue-500 font-semibold">
          Order Summary
        </h2>
        <div>
          <button
            className="border border-primary-blue-500 rounded-md flex bg-white text-sm font-semibold text-primary-blue-500 px-5 py-2 items-center"
            onClick={() => handleEmptyCart()}
          >
            <span className="mr-2">
              <EmptyCartIcon className="w-6 h-6" />
            </span>
            Empty Cart
          </button>
        </div>
      </div>
      {cart && (
        <CartOrderSummary
          cartData={cart}
          shippingLoading={false}
          addProductToCart={addProductToCart}
          removeProductFromCart={removeProductFromCart}
        />
      )}

      <div className="mt-6">
        <GuestShippingDetails
          cart={cart}
          callCart={callCart}
          shippingEditDetailEnabled={setEditDetailEnabled}
          editShippingEnabled={shippingEditDetailEnabled}
        />
      </div>
      {!shippingEditDetailEnabled && (
        <>
          <div
            className="text-blue-500 mb-3 text-sm underline underline-offset-2 cursor-pointer"
            onClick={enableEditShipping}
          >
            {'Edit Shipping Details'}
          </div>
          <label className="flex items-start  space-x-3 mb-2">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 mt-1"
              checked={shippingConfirmChecked}
              onChange={(e) => setShippingConfirmChecked(e.target.checked)}
            />
            <span className="font-normal text-sm">
              I confirm that all information provided, including my shipping
              address, is accurate and intended for delivery to me. I
              acknowledge that incorrect details may result in delivery delays
              and additional charges.
            </span>
          </label>
          <label className="flex items-center space-x-3 mb-2">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 mt-1"
              checked={userConsent}
              onChange={(e) => setUserConsent(e.target.checked)}
            />
            <span className="font-normal text-sm">
              I understand that my personal information will only be used for
              order-related communication purposes.
            </span>
          </label>
        </>
      )}
      {!!cart?.shipping_rate && !shippingEditDetailEnabled && (
        <div className="mt-7 w-full md:w-1/2 xl:w-1/4">
          <LoadingButton
            isLoading={payoutLoading}
            buttonText="Proceed to Pay"
            buttonIcon={<>{'💸'}</>}
            isDisabled={!shippingConfirmChecked || !userConsent}
            onClick={async () => {
              await proceedToPay();
            }}
          />
        </div>
      )}
    </div>
  );

  const renderCartContent = () => {
    if (cartDataLoading || pageRefresh) return <Loading />;
    if (cart?.line_items && cart.line_items.length > 0) return loadedData;
    return <>Your Cart is Empty!</>;
  };

  if (loading) return <Loading />;

  return (
    <div className="result-block-container bg-white shadow px-5 2xl:px-10 py-5 rounded-md relative mb-10">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            className: 'mr-3',
            href: '/guest/shopping-products',
            children: <>Home</>,
          },
          {
            tag: 'p',
            children: <span>Guest Cart</span>,
          },
        ]}
      />
      <h1 className="text-xl xl:text-3xl text-primary-blue-500 font-semibold mb-3">
        My Cart
      </h1>
      {renderCartContent()}
      {!isMobile().phone ? <FloatingCart /> : <FloatingCartMobile />}
    </div>
  );
};

export const GuestCart = () => {
  return (
    <GuestCartProvider>
      <GuestCartContent />
    </GuestCartProvider>
  );
};

export default GuestCart;
